import React, { useMemo, useRef, useState } from 'react'
import { Anchor, Box, Collapse, Group, Stack, Title } from '@mantine/core'

import {
  CrFormHandle,
  DEFAULT_STEP_STATE,
  deriveStepStatus,
} from './CrFormStep'
import { StepStatusControl } from './StepStatusControl'
import { INVALID_FIELD_SELECTOR, StepRegistryContext } from './useStepField'

// Vertical gap below each step; the connecting rail spans it to reach the next circle.
const STEP_GAP = 28
// Circle is 22px, so its center sits 11px from the top.
const CIRCLE_CENTER = 11

// A collapsible CR Form section, laid out as a timeline item: a status circle in a
// left rail (connected to the next step by a vertical line) and the foldable content
// on the right. All steps are shown at once and fold/unfold freely, in any order.
export const Step: React.FC<{
  // Step-state key: plain id for event-level steps, or "step:witnessIndex" per witness.
  id: string
  title: string
  form: CrFormHandle
  children?: React.ReactNode
  // The last step omits the trailing connector line.
  isLast?: boolean
  // Step to auto-expand once this one is completed (marked done via the status circle).
  nextStepId?: string
}> = ({ id, title, form, children, isLast = false, nextStepId }) => {
  // Field paths registered with this step (via useRegisterStepFields).
  const fieldsRef = useRef<Set<string>>(new Set())
  const stepRef = useRef<HTMLDivElement>(null)
  const [, forceRecompute] = useState(0)

  const registry = useMemo(
    () => ({
      register: (path: string) => {
        fieldsRef.current.add(path)
        forceRecompute(n => n + 1)
      },
      unregister: (path: string) => {
        fieldsRef.current.delete(path)
        forceRecompute(n => n + 1)
      },
    }),
    [],
  )

  const { expanded, done } = form.values.steps?.[id] ?? DEFAULT_STEP_STATE

  const hasError = [...fieldsRef.current].some(path => !!form.errors[path])
  const status = deriveStepStatus({ done, hasError })

  const setExpanded = (value: boolean) =>
    form.setFieldValue(`steps.${id}.expanded`, value)

  // The status circle toggles "done" regardless of whether the step is expanded, so a
  // collapsed, error-free step can be checked in one click (matching the DR script).
  const handleStatusClick = () => {
    // Already done → un-complete and re-open for editing.
    if (done) {
      form.setFieldValue(`steps.${id}.done`, false)
      form.setFieldValue(`steps.${id}.expanded`, true)
      return
    }

    // Not done → validate every field this step owns and mark done if clean. Map (not
    // every) so validateField runs for all paths: every short-circuits on the first error,
    // leaving later fields' errors unset so only the first invalid field would light up.
    const results = [...fieldsRef.current].map(path => form.validateField(path))
    const valid = results.every(result => !result.hasError)
    form.setFieldValue(`steps.${id}.done`, valid)
    if (valid) {
      // Collapse to declutter and advance the SA to the next step — but only if that step
      // isn't already done, so re-completing a step doesn't re-open a finished one.
      form.setFieldValue(`steps.${id}.expanded`, false)
      if (nextStepId && !form.values.steps?.[nextStepId]?.done) {
        form.setFieldValue(`steps.${nextStepId}.expanded`, true)
      }
    } else {
      // Invalid → expand so the errors are visible, then scroll to the first one.
      form.setFieldValue(`steps.${id}.expanded`, true)
      window.setTimeout(() => {
        stepRef.current
          ?.querySelector<HTMLElement>(INVALID_FIELD_SELECTOR)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 0)
    }
  }

  return (
    <Box
      ref={stepRef}
      data-cr-step={id}
      sx={{ position: 'relative', paddingBottom: isLast ? 0 : STEP_GAP }}
    >
      {!isLast && (
        <Box
          sx={theme => ({
            position: 'absolute',
            left: CIRCLE_CENTER - 1,
            top: CIRCLE_CENTER,
            bottom: -CIRCLE_CENTER,
            width: 2,
            backgroundColor: theme.colors.indigo[7],
          })}
        />
      )}

      <Group spacing={12} noWrap align="flex-start">
        <Box sx={{ position: 'relative', zIndex: 1, flex: 'none' }}>
          <StepStatusControl status={status} onClick={handleStatusClick} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Group spacing="xs" noWrap>
            <Anchor
              component="button"
              type="button"
              onClick={() => setExpanded(!expanded)}
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              <Title order={5} color="indigo.7">
                {title}
              </Title>
            </Anchor>
          </Group>

          <Collapse in={expanded}>
            <StepRegistryContext.Provider value={registry}>
              <Stack pt="md">{children}</Stack>
            </StepRegistryContext.Provider>
          </Collapse>
        </Box>
      </Group>
    </Box>
  )
}
